import {
  Dispatch,
  SetStateAction,
  useCallback,
  useState,
  useEffect,
  useMemo
} from 'react';
import { SurveyCakeLogicEngine } from '@surveycake/logic-engine';
import {
  useEngineInstance,
  useSubjectId,
  useSubjectAnswers
} from '@surveycake/react-logic-engine';

function useDigitSlideAnswerConfig(engine: SurveyCakeLogicEngine, id: number) {
  return useMemo(() => {
    const data = engine.getSubjectData(id);
    const min = data.lowerbounds!;
    const max = data.upperbounds!;
    return {
      min,
      max,
      step: data.increasebounds || 1,
      unit: data.unittxt || ''
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engine, id]);
}

function useDigitSlideAnswerValue(id: number, min: number) {
  const answersSelector = useCallback(
    answers => (answers.payload[0] as number) || min,
    [min]
  );
  const valueFromAnswer = useSubjectAnswers(id, answersSelector);
  const [value, setValue] = useState(valueFromAnswer);

  useEffect(() => {
    if (value !== valueFromAnswer) {
      setValue(valueFromAnswer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueFromAnswer]);

  return [value, setValue] as [number, Dispatch<SetStateAction<number>>];
}

function useHaveSetAnswerOnce(id: number) {
  const valueFromAnswer = useSubjectAnswers(id, answers => answers.payload[0]);
  // 0 is falsy
  return valueFromAnswer === 0 || Boolean(valueFromAnswer);
}

export function useDigitSlideAnswer() {
  const engine = useEngineInstance();
  const id = useSubjectId();
  const { min, max, step, unit } = useDigitSlideAnswerConfig(engine, id);
  const [value, onChange] = useDigitSlideAnswerValue(id, min);
  const haveSetAnswerOnce = useHaveSetAnswerOnce(id);
  const setAnswer = useCallback(
    (newValue: number) => {
      onChange(newValue);
      engine.setAnswer(id, { payload: [newValue] });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [engine, id]
  );

  return {
    min,
    max,
    step,
    unit,
    value,
    onChange,
    setAnswer,
    haveSetAnswerOnce
  };
}
